/**
 * Reading a hand back.
 *
 * Two pure functions and the shape of the wire they read. They live apart from
 * the component because they are the part with answers that can be wrong: a
 * pot that counts a bet nobody called is a pot that never existed, and a
 * position off by one turns "the button raised" into a different hand. Neither
 * needs a browser to be checked, so neither should need one to be tested.
 */

export type WireEvent =
  | { type: 'hand-started'; handNumber: number; buttonSeat: number; commitment: string }
  | { type: 'blind-posted'; seat: number; amount: number; blind: 'small' | 'big'; allIn: boolean }
  | { type: 'hole-cards-dealt'; seat: number; cards: string[] | null; cardCount: number }
  | { type: 'acted'; seat: number; action: string; amount: number; to: number; allIn: boolean; byTimeout: boolean }
  | { type: 'street-dealt'; street: string; cards: string[] }
  | { type: 'uncalled-returned'; seat: number; amount: number }
  | { type: 'pots-built'; pots: { amount: number; eligible: number[] }[] }
  | { type: 'showdown'; shown: { seat: number; cards: string[] }[] }
  | { type: 'awarded'; seat: number; amount: number; potIndex: number }
  | { type: 'hand-complete'; handNumber: number };

interface ReplayAction {
  seat: number;
  label: string;
  kind: string;
  /** Null where an amount would be noise — a check has no size. */
  amount: number | null;
}

interface ReplayStreet {
  name: string;
  cards: string[];
  actions: ReplayAction[];
  /** What the pot stood at when the street ended. */
  pot: number;
}

/**
 * Cut the event log into the four streets.
 *
 * The pot is accumulated rather than read off `pots-built`, which only fires
 * once at the end: what a player wants beside the flop is what the flop cost,
 * and that is the sum of everything committed by the time it was over.
 */

export function cutIntoStreets(events: readonly WireEvent[]): ReplayStreet[] {
  const streets: ReplayStreet[] = [{ name: 'preflop', cards: [], actions: [], pot: 0 }];
  // What each seat has put in on this street, so a raise "to 6" adds only the
  // difference — the wire says where a bet landed, not what it cost.
  let onStreet = new Map<number, number>();
  let pot = 0;

  const current = (): ReplayStreet => streets[streets.length - 1] as ReplayStreet;
  const commit = (seat: number, to: number): void => {
    const already = onStreet.get(seat) ?? 0;
    if (to > already) {
      pot += to - already;
      onStreet.set(seat, to);
    }
    current().pot = pot;
  };

  for (const event of events) {
    switch (event.type) {
      case 'blind-posted':
        commit(event.seat, event.amount);
        current().actions.push({
          seat: event.seat,
          label: event.blind === 'small' ? 'SB' : 'BB',
          kind: 'blind',
          amount: event.amount,
        });
        break;

      case 'acted': {
        if (event.action !== 'fold' && event.action !== 'check') commit(event.seat, event.to);
        current().actions.push({
          seat: event.seat,
          label: event.allIn ? 'ALL IN' : event.action.toUpperCase(),
          kind: event.allIn ? 'allin' : event.action,
          amount: event.action === 'fold' || event.action === 'check' ? null : event.to,
        });
        break;
      }

      case 'uncalled-returned':
        // A bet nobody matched never really entered the pot, and showing it as
        // if it had makes the flop look bigger than it was.
        pot -= event.amount;
        current().pot = pot;
        current().actions.push({
          seat: event.seat,
          label: 'RETURN',
          kind: 'return',
          amount: event.amount,
        });
        break;

      case 'street-dealt':
        streets.push({ name: event.street, cards: [...event.cards], actions: [], pot });
        onStreet = new Map();
        break;

      case 'awarded':
        current().actions.push({
          seat: event.seat,
          label: 'WINS',
          kind: 'wins',
          amount: event.amount,
        });
        break;

      default:
        break;
    }
  }
  return streets;
}

/**
 * The name of each seat's position, which is what a hand is actually read by.
 *
 * "Seat 4 raised" tells you nothing. "The button raised" is the hand. Heads-up
 * has its own rule, as it does everywhere else in poker: the button posts the
 * small blind, so there is no third position to name.
 */
export function positionsOf(seats: readonly number[], buttonSeat: number): Map<number, string> {
  const order = [...seats].sort((a, b) => a - b);
  const places = new Map<number, string>();
  if (order.length < 2) return places;

  const buttonIndex = Math.max(0, order.indexOf(buttonSeat));
  const from = (offset: number): number => order[(buttonIndex + offset) % order.length] as number;

  if (order.length === 2) {
    places.set(from(0), 'SB/BTN');
    places.set(from(1), 'BB');
    return places;
  }

  places.set(from(0), 'BTN');
  places.set(from(1), 'SB');
  places.set(from(2), 'BB');
  // Everyone between the big blind and the button, counted from the front:
  // first to act is under the gun, last before the button is the cutoff.
  const rest = order.length - 3;
  for (let i = 0; i < rest; i += 1) {
    const seat = from(3 + i);
    if (i === rest - 1) places.set(seat, 'CO');
    else if (i === 0) places.set(seat, 'UTG');
    else places.set(seat, `UTG+${i}`);
  }
  return places;
}
